// T028: SharedSecrets create component with file upload
// Feature: 010-ios-android-zenky
// FIXED: Now using useDataProvider instead of direct fetch
import {
    Create,
    TabbedForm,
    FormTab,
    TextInput,
    DateInput,
    required,
    useNotify,
    useRedirect,
    useDataProvider,
} from 'react-admin';
import { useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import type { LogtoDataProvider } from '../../dataProvider';

export const SharedSecretsCreate = () => {
    const [iosCertFile, setIosCertFile] = useState<File | null>(null);
    const [iosProfileFile, setIosProfileFile] = useState<File | null>(null);
    const [androidKeystoreFile, setAndroidKeystoreFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);

    const dataProvider = useDataProvider<LogtoDataProvider>();
    const notify = useNotify();
    const redirect = useRedirect();

    const handleIOSUpload = async (formData: any) => {
        if (!iosCertFile || !iosProfileFile) {
            notify('Please select both certificate and provisioning profile files', { type: 'error' });
            return;
        }

        setUploading(true);
        try {
            await dataProvider.uploadSharedIOSCertificate({
                certificate: iosCertFile,
                provisioning_profile: iosProfileFile,
                certificate_password: formData.certificatePassword,
                certificate_id: formData.certificateId,
                certificate_expiration: formData.certificateExpiration,
                profile_id: formData.profileId,
                profile_expiration: formData.profileExpiration,
            });

            notify('iOS certificate uploaded successfully', { type: 'success' });
            redirect('/shared-secrets');
        } catch (error) {
            notify('Failed to upload iOS certificate', { type: 'error' });
            console.error('iOS upload error:', error);
        } finally {
            setUploading(false);
        }
    };

    const handleAndroidUpload = async (formData: any) => {
        if (!androidKeystoreFile) {
            notify('Please select a keystore file', { type: 'error' });
            return;
        }

        setUploading(true);
        try {
            await dataProvider.uploadSharedAndroidKeystore({
                keystore: androidKeystoreFile,
                keystore_password: formData.keystorePassword,
                key_alias: formData.keyAlias,
                key_password: formData.keyPassword,
            });

            notify('Android keystore uploaded successfully', { type: 'success' });
            redirect('/shared-secrets');
        } catch (error) {
            notify('Failed to upload Android keystore', { type: 'error' });
            console.error('Android upload error:', error);
        } finally {
            setUploading(false);
        }
    };

    return (
        <Create title="Upload Shared Zenky Account Credentials">
            <TabbedForm>
                <FormTab label="iOS Certificate">
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                        Upload an iOS certificate (.p12) and provisioning profile (.mobileprovision) for the shared Zenky account.
                    </Typography>

                    <Box sx={{ mb: 2 }}>
                        <input
                            type="file"
                            accept=".p12"
                            onChange={(e) => setIosCertFile(e.target.files?.[0] || null)}
                            style={{ display: 'block', marginBottom: '8px' }}
                        />
                        <Typography variant="caption">
                            {iosCertFile ? `Selected: ${iosCertFile.name}` : 'No certificate file selected'}
                        </Typography>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                        <input
                            type="file"
                            accept=".mobileprovision"
                            onChange={(e) => setIosProfileFile(e.target.files?.[0] || null)}
                            style={{ display: 'block', marginBottom: '8px' }}
                        />
                        <Typography variant="caption">
                            {iosProfileFile ? `Selected: ${iosProfileFile.name}` : 'No provisioning profile selected'}
                        </Typography>
                    </Box>

                    <TextInput
                        source="certificatePassword"
                        label="Certificate Password"
                        type="password"
                        validate={required()}
                        fullWidth
                    />

                    <TextInput
                        source="certificateId"
                        label="Certificate ID (Optional)"
                        fullWidth
                    />
                    <DateInput
                        source="certificateExpiration"
                        label="Certificate Expiration (Optional)"
                        fullWidth
                    />
                    <TextInput
                        source="profileId"
                        label="Profile ID (Optional)"
                        fullWidth
                    />
                    <DateInput
                        source="profileExpiration"
                        label="Profile Expiration (Optional)"
                        fullWidth
                    />

                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => {
                            const formData = {
                                certificatePassword: (document.querySelector('input[name="certificatePassword"]') as HTMLInputElement)?.value,
                                certificateId: (document.querySelector('input[name="certificateId"]') as HTMLInputElement)?.value,
                                certificateExpiration: (document.querySelector('input[name="certificateExpiration"]') as HTMLInputElement)?.value,
                                profileId: (document.querySelector('input[name="profileId"]') as HTMLInputElement)?.value,
                                profileExpiration: (document.querySelector('input[name="profileExpiration"]') as HTMLInputElement)?.value,
                            };
                            handleIOSUpload(formData);
                        }}
                        disabled={!iosCertFile || !iosProfileFile || uploading}
                    >
                        {uploading ? 'Uploading...' : 'Upload iOS Certificate'}
                    </Button>
                </FormTab>

                <FormTab label="Android Keystore">
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                        Upload an Android keystore (.jks or .keystore) for the shared Zenky account.
                    </Typography>

                    <Box sx={{ mb: 2 }}>
                        <input
                            type="file"
                            accept=".jks,.keystore"
                            onChange={(e) => setAndroidKeystoreFile(e.target.files?.[0] || null)}
                            style={{ display: 'block', marginBottom: '8px' }}
                        />
                        <Typography variant="caption">
                            {androidKeystoreFile ? `Selected: ${androidKeystoreFile.name}` : 'No keystore file selected'}
                        </Typography>
                    </Box>

                    <TextInput
                        source="keystorePassword"
                        label="Keystore Password"
                        type="password"
                        validate={required()}
                        fullWidth
                    />
                    <TextInput
                        source="keyAlias"
                        label="Key Alias"
                        validate={required()}
                        fullWidth
                    />
                    <TextInput
                        source="keyPassword"
                        label="Key Password"
                        type="password"
                        validate={required()}
                        fullWidth
                    />

                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => {
                            const formData = {
                                keystorePassword: (document.querySelector('input[name="keystorePassword"]') as HTMLInputElement)?.value,
                                keyAlias: (document.querySelector('input[name="keyAlias"]') as HTMLInputElement)?.value,
                                keyPassword: (document.querySelector('input[name="keyPassword"]') as HTMLInputElement)?.value,
                            };
                            handleAndroidUpload(formData);
                        }}
                        disabled={!androidKeystoreFile || uploading}
                    >
                        {uploading ? 'Uploading...' : 'Upload Android Keystore'}
                    </Button>
                </FormTab>
            </TabbedForm>
        </Create>
    );
};
